// Lista de formas de pago de un cobro (wizard, enganche y carrito): reglas, cuadre y body. Puro.
import {
    RETAIL_CARD_LAST4_PATTERN,
    RETAIL_CLABE_PATTERN,
    RETAIL_SALE_ID_TEXT_PATTERN,
    RETAIL_SPEI_TRACKING_KEY_PATTERN,
} from '@fiado/type-kit/bin/retailIntegration/constants/retailIntegrationLimits';
import {
    MAX_SALE_PAYMENTS,
    SINGLE_USE_PAYMENT_METHODS,
} from '@fiado/type-kit/bin/retailWizard/constants/retailWizardValidation';
import { SalePaymentMethodEnum } from '@fiado/type-kit/bin/retailWizard/enums/SalePaymentMethodEnum';
import { ApiError } from '@/api/errors';
import type { components } from '@/api/services/retail-wizard/retail-wizard.gen';
import { formatMoneyCents } from '@/shared/lib/format/money';
import type { PaymentMethod } from './types';

export type SalePayment = components['schemas']['SalePayment'];

export const MAX_PAYMENTS = MAX_SALE_PAYMENTS;

const SCREEN_METHOD: Record<SalePaymentMethodEnum, PaymentMethod> = {
    [SalePaymentMethodEnum.CASH]: 'CASH',
    [SalePaymentMethodEnum.CARD]: 'CARD',
    [SalePaymentMethodEnum.WIRE]: 'SPEI',
};

/** Medios que van a lo más una vez por cobro, en el vocabulario de pantalla (`WIRE` es `SPEI`). */
const SINGLE_USE_METHODS: readonly PaymentMethod[] = SINGLE_USE_PAYMENT_METHODS.map(
    (m): PaymentMethod => SCREEN_METHOD[m],
);

/** Orden en que se ofrece el medio de un renglón nuevo. */
const METHOD_ORDER: readonly PaymentMethod[] = ['CASH', 'CARD', 'SPEI'];

/** Un renglón del cobro tal como lo captura el vendedor. */
export interface PaymentDraft {
    id: string;
    method: PaymentMethod;
    /** Con un solo renglón no cuenta: el monto es el total. */
    amountCents: number;
    cashOk: boolean;
    cardLast4: string;
    authCode: string;
    speiKey: string;
    senderClabe: string;
}

export type ReceiptField = 'cardLast4' | 'authCode' | 'speiKey' | 'senderClabe';

export interface PaymentsBalance {
    assignedCents: number;
    /** Positivo si falta, negativo si sobra. */
    missingCents: number;
    status: 'MATCH' | 'SHORT' | 'OVER';
}

export function createPaymentDraft(id: string, method: PaymentMethod): PaymentDraft {
    return {
        id,
        method,
        amountCents: 0,
        cashOk: false,
        cardLast4: '',
        authCode: '',
        speiKey: '',
        senderClabe: '',
    };
}

export function initialPaymentDrafts(id = 'payment-1'): PaymentDraft[] {
    return [createPaymentDraft(id, 'CASH')];
}

/** Otro renglón ya usa ese medio de uso único. */
export function isMethodTaken(
    drafts: readonly PaymentDraft[],
    method: PaymentMethod,
    exceptId: string,
): boolean {
    if (!SINGLE_USE_METHODS.includes(method)) return false;
    return drafts.some((d) => d.id !== exceptId && d.method === method);
}

/** Medio de un renglón nuevo: el primero libre; la tarjeta siempre lo está. */
export function defaultMethodFor(drafts: readonly PaymentDraft[]): PaymentMethod {
    return METHOD_ORDER.find((m) => !isMethodTaken(drafts, m, '')) ?? 'CARD';
}

export function paymentAmountCents(
    drafts: readonly PaymentDraft[],
    draft: PaymentDraft,
    totalCents: number,
): number {
    return drafts.length === 1 ? totalCents : draft.amountCents;
}

export function paymentsBalance(
    drafts: readonly PaymentDraft[],
    totalCents: number,
): PaymentsBalance {
    const assignedCents = drafts.reduce(
        (sum, d) => sum + paymentAmountCents(drafts, d, totalCents),
        0,
    );
    const missingCents = totalCents - assignedCents;
    const status = missingCents === 0 ? 'MATCH' : missingCents > 0 ? 'SHORT' : 'OVER';
    return { assignedCents, missingCents, status };
}

/** Agrega un renglón con lo que falta; el primero fija el total que traía implícito. */
export function addPaymentDraft(
    drafts: readonly PaymentDraft[],
    totalCents: number,
    id: string,
): readonly PaymentDraft[] {
    if (drafts.length >= MAX_PAYMENTS) return drafts;
    const fixed = drafts.map((d) => ({
        ...d,
        amountCents: paymentAmountCents(drafts, d, totalCents),
    }));
    const missing = Math.max(0, paymentsBalance(fixed, totalCents).missingCents);
    return [...fixed, { ...createPaymentDraft(id, defaultMethodFor(fixed)), amountCents: missing }];
}

export function removePaymentDraft(
    drafts: readonly PaymentDraft[],
    id: string,
): readonly PaymentDraft[] {
    if (drafts.length <= 1) return drafts;
    return drafts.filter((d) => d.id !== id);
}

const RECEIPT_MESSAGES: Record<ReceiptField, string> = {
    cardLast4: 'Son 4 dígitos. Si no los tienes, deja el campo vacío.',
    authCode: 'De 1 a 64 caracteres. Si no la tienes, deja el campo vacío.',
    speiKey: 'De 1 a 30 letras o números, sin espacios. Si no la tienes, deja el campo vacío.',
    senderClabe: 'Son 18 dígitos. Si no la tienes, deja el campo vacío.',
};

const RECEIPT_RULES: Record<ReceiptField, { method: PaymentMethod; pattern: RegExp }> = {
    cardLast4: { method: 'CARD', pattern: RETAIL_CARD_LAST4_PATTERN },
    authCode: { method: 'CARD', pattern: RETAIL_SALE_ID_TEXT_PATTERN },
    speiKey: { method: 'SPEI', pattern: RETAIL_SPEI_TRACKING_KEY_PATTERN },
    senderClabe: { method: 'SPEI', pattern: RETAIL_CLABE_PATTERN },
};

/** Errores de formato del comprobante del medio; un campo vacío no es error. */
export function receiptErrors(draft: PaymentDraft): Partial<Record<ReceiptField, string>> {
    const errors: Partial<Record<ReceiptField, string>> = {};
    for (const field of Object.keys(RECEIPT_RULES) as ReceiptField[]) {
        const rule = RECEIPT_RULES[field];
        const value = draft[field];
        if (rule.method === draft.method && value !== '' && !rule.pattern.test(value)) {
            errors[field] = RECEIPT_MESSAGES[field];
        }
    }
    return errors;
}

/** Monto pendiente o excedente que se nombra en el botón; `null` si cuadra. */
export function balanceBlocker(balance: PaymentsBalance): string | null {
    if (balance.status === 'SHORT') {
        return `Asigna los ${formatMoneyCents(balance.missingCents)} que faltan`;
    }
    return balance.status === 'OVER' ? 'Ajusta los montos' : null;
}

function hasRepeatedSingleUse(drafts: readonly PaymentDraft[]): boolean {
    return SINGLE_USE_METHODS.some((m) => drafts.filter((d) => d.method === m).length > 1);
}

/** Lo primero que impide registrar el cobro; `null` si ya se puede. */
export function paymentsBlocker(
    drafts: readonly PaymentDraft[],
    totalCents: number,
): string | null {
    if (totalCents === 0) return null;
    if (drafts.length > 1 && drafts.some((d) => d.amountCents < 1)) {
        return 'Captura el monto de cada forma de pago.';
    }
    const balance = balanceBlocker(paymentsBalance(drafts, totalCents));
    if (balance) return balance;
    if (hasRepeatedSingleUse(drafts)) {
        return 'Efectivo y transferencia van una sola vez en el cobro.';
    }
    if (drafts.some((d) => d.method === 'CASH' && !d.cashOk)) {
        return 'Confirma que el cliente pagó en la caja.';
    }
    if (drafts.some((d) => Object.keys(receiptErrors(d)).length > 0)) {
        return 'Corrige el comprobante o deja el campo vacío.';
    }
    return null;
}

function receiptValue(draft: PaymentDraft, field: ReceiptField): string | null {
    const value = draft[field];
    return RECEIPT_RULES[field].method === draft.method && value !== '' ? value : null;
}

/** `payments` del body; `undefined` en un cobro de $0. La transferencia viaja como `WIRE`. */
export function toSalePayments(
    drafts: readonly PaymentDraft[],
    totalCents: number,
): SalePayment[] | undefined {
    if (totalCents === 0) return undefined;
    return drafts.map((d) => ({
        method: d.method === 'SPEI' ? 'WIRE' : d.method,
        amountCents: paymentAmountCents(drafts, d, totalCents),
        cashConfirmedInRegister: d.method === 'CASH' ? d.cashOk : null,
        cardLast4: receiptValue(d, 'cardLast4'),
        authCode: receiptValue(d, 'authCode'),
        speiTrackingKey: receiptValue(d, 'speiKey'),
        senderClabe: receiptValue(d, 'senderClabe'),
    }));
}

const SALE_PAYMENT_MESSAGES: Record<string, string> = {
    TOO_MANY_PAYMENTS: 'Un cobro lleva máximo 3 formas de pago. Quita una e intenta de nuevo.',
    PAYMENT_METHOD_REPEATED:
        'Efectivo y transferencia van una sola vez en el cobro. Junta ese monto en un solo renglón.',
    PAYMENT_AMOUNT_INVALID: 'Cada forma de pago necesita un monto mayor a $0.',
    PAYMENTS_REQUIRED: 'Captura cómo pagó el cliente antes de registrar el cobro.',
    CARD_LAST4_INVALID:
        'Los últimos 4 dígitos de la tarjeta son 4 números. Corrígelos o deja el campo vacío.',
    CARD_AUTH_INVALID: 'El número de autorización no es válido. Corrígelo o deja el campo vacío.',
    SPEI_TRACKING_INVALID:
        'La clave de rastreo lleva de 1 a 30 letras o números, sin espacios. Corrígela o deja el campo vacío.',
    SENDER_CLABE_INVALID: 'La CLABE ordenante son 18 dígitos. Corrígela o deja el campo vacío.',
};

function totalMismatchMessage(error: ApiError): string {
    const data = (error.data ?? {}) as { expected?: unknown; got?: unknown };
    if (typeof data.expected === 'number' && typeof data.got === 'number') {
        return `Las formas de pago suman ${formatMoneyCents(data.got)} y el total es ${formatMoneyCents(data.expected)}. Ajusta los montos.`;
    }
    return 'La suma de las formas de pago no cuadra con el total. Ajusta los montos.';
}

/** Rechazo de la lista de pagos (400 del silo) → qué corrige el vendedor; `undefined` si no es de aquí. */
export function salePaymentErrorMessage(error: unknown): string | undefined {
    if (!(error instanceof ApiError)) return undefined;
    if (error.code === 'PAYMENTS_TOTAL_MISMATCH') return totalMismatchMessage(error);
    return Object.hasOwn(SALE_PAYMENT_MESSAGES, error.code)
        ? SALE_PAYMENT_MESSAGES[error.code]
        : undefined;
}
